import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ACCENT,
  DATA_DIVERGENT_600,
  ON_SURFACE_DISABLED,
} from '@sama/design-tokens';
import { isDefined } from '@sama/js-core';
import { partition } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ProgressBarComponent } from '../../../cross-team-shared/components/progress-bar/progress-bar.component';
import { SampleTaskNavigationService } from '../services/sample-task-navigation.service';
import { SampleTaskInternal } from '../state/sampling-workspace-state';
import { SamplingWorkspaceStore } from '../state/sampling-workspace-store';

@Component({
  selector: 'h3-sample-task-review-status',
  templateUrl: './sample-task-review-status.component.html',
  styleUrl: './sample-task-review-status.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ProgressBarComponent,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatBadgeModule,
    OverlayModule,
  ],
})
export class SampleTaskReviewStatusComponent {
  private readonly store = inject(SamplingWorkspaceStore);
  totalTaskNumber = this.store.state().sampleTasks.length;
  categories$ = this.store('sampleTasks').$.pipe(
    map(() => {
      const { reviewedWithNoFeedback, reviewedWithFeedback, notReviewed } =
        this.getReviewCategories();
      return [
        {
          name: 'ReviewedWithNoFeedback',
          color: ACCENT,
          percentage:
            (reviewedWithNoFeedback.length / this.totalTaskNumber) * 100,
          tooltip: `No Feedback: ${reviewedWithNoFeedback.length}`,
        },
        {
          name: 'ReviewedWithFeedback',
          color: DATA_DIVERGENT_600,
          percentage:
            (reviewedWithFeedback.length / this.totalTaskNumber) * 100,
          tooltip: `Feedback: ${reviewedWithFeedback.length}`,
        },
        {
          name: 'Unreviewed',
          color: ON_SURFACE_DISABLED,
          percentage: (notReviewed.length / this.totalTaskNumber) * 100,
          tooltip: `To Review: ${notReviewed.length}`,
        },
      ];
    }),
  );
  position = this.store.state().position;
  taskStatusList$ = this.store('sampleTasks').$;
  showPanel$ = new BehaviorSubject(false);
  private taskNavigationService = inject(SampleTaskNavigationService);

  togglePanel() {
    this.showPanel$.next(!this.showPanel$.value);
  }

  closePanel() {
    this.showPanel$.next(false);
  }

  goToTask(position: number) {
    if (position === this.position) {
      return;
    }
    this.taskNavigationService.navigateToTask(position, 'task_status_list');
  }

  getFeedbackCount(task: SampleTaskInternal) {
    const feedbackCount = task.feedbackCount;
    return isDefined(feedbackCount) && feedbackCount > 0
      ? task.feedbackCount
      : undefined;
  }

  private getReviewCategories() {
    const tasks = this.store.state().sampleTasks;
    const [reviewed, notReviewed] = partition(tasks, (task) => task.reviewed);
    const [reviewedWithFeedback, reviewedWithNoFeedback] = partition(
      reviewed,
      (task) => task.feedbackCount,
    );
    return {
      reviewedWithNoFeedback,
      reviewedWithFeedback,
      notReviewed,
    };
  }
}
